'use client'

import { useState, useEffect } from 'react'
import { FaqService } from '../../lib/services'
import { FaqItem } from '../../lib/services'

export default function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [openItems, setOpenItems] = useState<number[]>([])
  const [faqs, setFaqs] = useState<FaqItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Dados locais como fallback
  const fallbackFaqs: FaqItem[] = [
    {
      id: 1,
      pergunta: 'Como funciona o processo de agendamento de exames?',
      resposta: 'O processo é simples: insira os dados da empresa (CNPJ ou CPF), cadastre os colaboradores com suas informações, escolha a data e clínica desejada, revise o pedido e faça o pagamento. Suas guias são geradas automaticamente e enviadas por email.',
      ordem: 1,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 2,
      pergunta: 'Quais tipos de exames posso agendar?',
      resposta: 'Oferecemos exames admissionais, demissionais, periódicos, mudança de função, retorno ao trabalho e avaliações psicossociais. Também é possível adicionar exames complementares específicos para cada colaborador.',
      ordem: 2,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 3,
      pergunta: 'Como funciona o pagamento?',
      resposta: 'Aceitamos cartão de crédito e PIX. O pagamento por cartão é processado imediatamente, enquanto o PIX gera um código QR para pagamento instantâneo. As guias são liberadas após a confirmação do pagamento.',
      ordem: 3,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 4,
      pergunta: 'Posso personalizar os exames para cada colaborador?',
      resposta: 'Sim! Você pode definir exames específicos para cada função, adicionar exames complementares individuais e até mesmo incluir informações sobre médicos coordenadores quando necessário.',
      ordem: 4,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 5,
      pergunta: 'Quanto tempo leva para receber as guias?',
      resposta: 'As guias são geradas automaticamente após a confirmação do pagamento e enviadas imediatamente por email. Você também pode fazer o download direto da plataforma.',
      ordem: 5,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 6,
      pergunta: 'Posso agendar exames para qualquer estado?',
      resposta: 'Sim! Temos uma rede de mais de 2000 clínicas parceiras distribuídas por todo o território nacional, cobrindo todos os estados brasileiros.',
      ordem: 6,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 7,
      pergunta: 'Como funciona o atendimento online?',
      resposta: 'Para exames marcados como online, o colaborador receberá um link para participar da consulta por videochamada. Todo o processo é feito de forma segura e confidencial.',
      ordem: 7,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 8,
      pergunta: 'Posso cancelar ou alterar um agendamento?',
      resposta: 'Cancelamentos podem ser feitos até 48 horas antes da data agendada. Para alterações, entre em contato conosco pelo Fale Conosco. Algumas condições podem aplicar taxas.',
      ordem: 8,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 9,
      pergunta: 'Como funciona o sistema de descontos?',
      resposta: 'Oferecemos descontos por volume e programas de fidelidade. Empresas que fazem agendamentos regulares podem acumular créditos para utilizar em pedidos futuros.',
      ordem: 9,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 10,
      pergunta: 'Os exames são compatíveis com o e-Social?',
      resposta: 'Sim! Todos os nossos exames seguem as normas regulamentadoras e geram documentação compatível com o sistema e-Social, facilitando o cumprimento das obrigações trabalhistas.',
      ordem: 10,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 11,
      pergunta: 'Como faço para incluir meu médico coordenador?',
      resposta: 'Durante o cadastro da empresa, você pode informar se possui PCMSO e incluir os dados do médico responsável. Ele será automaticamente associado aos resultados dos exames.',
      ordem: 11,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    {
      id: 12,
      pergunta: 'Posso agendar exames para estagiários e menores aprendizes?',
      resposta: 'Sim! Temos categorias específicas para jovens aprendizes e estagiários, com exames apropriados para cada faixa etária conforme determina a legislação.',
      ordem: 12,
      enabled: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  ]

  // Buscar FAQs da API
  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const apiFaqs = await FaqService.getFaqs()
        setFaqs(apiFaqs)
      } catch (error) {
        console.error('Erro ao carregar FAQs da API:', error)
        // Usar dados locais como fallback
        setFaqs(fallbackFaqs)
      } finally {
        setLoading(false)
      }
    }

    loadFaqs()
  }, [])

  const filteredFaqs = faqs.filter(faq =>
    faq.pergunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.resposta.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const toggleItem = (id: number | string) => {
    const numId = typeof id === 'string' ? parseInt(id) : id
    setOpenItems(prev =>
      prev.includes(numId)
        ? prev.filter(item => item !== numId)
        : [...prev, numId]
    )
  }

  // Removido: categoria não existe na API

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Dúvidas Frequentes
            </h1>
            <p className="text-lg text-gray-600">
              Encontre respostas para as perguntas mais comuns sobre nossos serviços.
            </p>
          </div>

          {/* Search */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="relative">
              <svg className="absolute left-3 top-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Buscar perguntas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white text-gray-900"
              />
            </div>
          </div>

          {/* Search Filter Clear */}
          {searchTerm && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
              <div className="flex items-center justify-between">
                <p className="text-gray-600">
                  Filtrando por: <span className="font-semibold text-gray-900">"{searchTerm}"</span>
                </p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                >
                  Limpar filtro
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Carregando perguntas frequentes...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* FAQ List */}
          {!loading && (
            <div className="bg-white rounded-lg shadow-sm">
              {filteredFaqs.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500">Nenhuma pergunta encontrada para "{searchTerm}"</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredFaqs.map((faq) => (
                    <div key={faq.id} className="p-6">
                      <button
                        onClick={() => toggleItem(faq.id)}
                        className="w-full flex justify-between items-start text-left hover:bg-gray-50 p-2 rounded"
                      >
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900">
                            {faq.pergunta}
                          </h3>
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-500 ml-4 mt-1 transform transition-transform ${openItems.includes(faq.id) ? 'rotate-180' : ''
                            }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {openItems.includes(typeof faq.id === 'string' ? parseInt(faq.id) : faq.id) && (
                        <div className="mt-4 pl-2">
                          <div
                            className="text-gray-700 leading-relaxed prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: faq.resposta }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contact CTA */}
          <div className="text-center mt-12">
            <div className="bg-green-50 border border-green-200 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-green-900 mb-4">
                Não encontrou sua dúvida?
              </h3>
              <p className="text-green-700 mb-6">
                Entre em contato conosco! Nossa equipe está pronta para ajudar.
              </p>
              <a
                href="/fale-conosco"
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                Fale Conosco
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

